import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { message } from 'antd';
import {
  getClassKnowledges,
  getClassSpuKnowledgeDetail,
  getClassSpus,
} from '@knowledge/service/api/single';
import { useRequest } from 'ahooks';
import produce from 'immer';
import { useTranslate } from '@tolgee/react';
import { currentClassId, currentKcList, currentSpuList, allStoreList } from './state';
import { useFormikContext } from 'formik';
import { useCallback, useEffect } from 'react';
import { useSpuKnowledgeMutate } from '@knowledge/knowledges/spu/crud';
import { noop, sortBy } from 'lodash-es';
import { getMultiLangValue } from './helpers';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import {
  groupBy,
  useGetAdminStoresByStoreType,
} from '@knowledge/knowledges/general/edit/selectors/StoreSelect';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';

let classKcListRefreshFn = noop;

export const useClassKcList = () => {
  const { t } = useTranslate();
  const classId = useAtomValue(currentClassId);
  const [kcList, setKcList] = useAtom(currentKcList);
  const { loading, refresh } = useRequest(() => getClassKnowledges(classId!), {
    refreshDeps: [classId],
    ready: !!classId,
    onSuccess: (data) => {
      setKcList(
        produce(kcList, (draft) => {
          draft.splice(0, draft.length, ...data);
        }),
      );
    },
    onError: () => {
      setKcList([]);
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  useEffect(() => {
    classKcListRefreshFn = refresh;
  });
  return {
    data: kcList,
    loading,
    refresh,
  };
};

export const refreshKcList = () => {
  classKcListRefreshFn();
};

export const useClassSpuList = () => {
  const { t } = useTranslate();
  const classId = useAtomValue(currentClassId);
  const [spuList, setSpuList] = useAtom(currentSpuList);
  const { loading, data } = useRequest(() => getClassSpus(classId!), {
    refreshDeps: [classId],
    ready: !!classId,
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  const { loading: loading2 } = useRequest(() => getItemsByInternalIds(data || []), {
    refreshDeps: [data],
    ready: !!data,
    onSuccess: (data) => {
      const list = data;
      setSpuList(list);
    },
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  return {
    data: spuList,
    loading: loading || loading2,
  };
};

export const useRefreshCurrentKnowledge = () => {
  const { values, setValues } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const classId = useAtomValue(currentClassId);
  const setKcList = useSetAtom(currentKcList);
  const lang = useSystemLanguageValue();
  const { t } = useTranslate();
  const { runAsync } = useRequest(() => getClassSpuKnowledgeDetail(classId, values.id), {
    manual: true,
    onSuccess: (data) => {
      setKcList(
        produce((draft) => {
          const index = draft.findIndex((item) => item.id === data.id);
          if (index !== -1) {
            draft[index] = data;
          }
        }),
      );
      // ConfigItem 的 Formik 已不再开启 enableReinitialize（见 task 11167 的修复），
      // 服务端详情回灌后必须显式写回表单，否则知识值列表会继续渲染编辑前的旧值。
      // 这里的字段派生方式与 ConfigItem 的 initialValues 保持一致：知识名按系统语言取多语言值，
      // 知识值按默认值优先排序（与 ConfigPanel 的展示顺序一致）。
      setValues({
        ...data,
        knowledgeName: getMultiLangValue(data.nameMultiLang, lang, data.knowledgeName),
        values: sortBy(data.values, (item) => (item.isDefaultValue ? 0 : 1)),
      } as Knowledge.SingleClass.Knowledge);
    },
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  return runAsync;
};

export const useUpdateOneKnowledgeInKcList = () => {
  const { values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const { t } = useTranslate();
  const { update, mutateLoading } = useSpuKnowledgeMutate();
  const classId = useAtomValue(currentClassId);
  const setKcList = useSetAtom(currentKcList);
  const { loading: loading2, runAsync } = useRequest(
    () => getClassSpuKnowledgeDetail(classId, values.id),
    {
      manual: true,
      onSuccess: (data) => {
        setKcList(
          produce((draft) => {
            const index = draft.findIndex((item) => item.id === data.id);
            if (index !== -1) {
              draft[index] = data;
            }
          }),
        );
      },
    },
  );
  const updateFn = useCallback(
    (pValues) => {
      return update({
        spuKnowledge: pValues,
      }).then(() => {
        runAsync();
      });
    },
    [update, runAsync],
  );

  const deleteUpdateFn = useCallback(
    (pValues) => {
      return update({
        spuKnowledge: pValues,
      }).then(() => {
        message.success(t('phrase.删除成功'));
        runAsync();
      });
    },
    [update, t, runAsync],
  );
  return {
    update: updateFn,
    deleteUpdate: deleteUpdateFn,
    loading: mutateLoading || loading2,
  };
};

export const useStoreDataSource = () => {
  const { data: storeDataSource, loading } = useGetAdminStoresByStoreType('ALL');
  const [allStore, setAllStore] = useAtom(allStoreList);

  useEffect(() => {
    setAllStore(groupBy(storeDataSource));
  }, [storeDataSource, setAllStore]);

  return {
    data: allStore,
    loading,
  };
};
